import React from "react";
import { observer } from "mobx-react";
import SectionSources from "./SectionSources";

const RenderRow = ({ label, value }) => {
  if (value === null || value === undefined || value === "") return null;
  if (Array.isArray(value) && value.length === 0) return null;
  return (
    <tr>
      <td>{label}</td>
      <td>{Array.isArray(value) ? value.join(", ") : (typeof value === "object" ? JSON.stringify(value) : value)}</td>
    </tr>
  );
};

const formatKey = (key) => key.split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

const formatHQ = (company) => {
  if (!company) return null;
  if (company.offices && company.offices[0]) {
    const o = company.offices[0];
    return [o.city, o.state, o.country].filter(Boolean).join(", ");
  }
  return [company.city, company.state, company.country].filter(Boolean).join(", ") || null;
};

const CompanyOverview = observer(({ companyData, companyInsights, deepDiveIntel, serpLinks, accountId, sellerCompetitorNames }) => {
  // Real shape:
  //   companyData.company_data → { name, founded, industry[], revenue, followers, description, offices[], specialities[], website, ... }
  //   companyData.web_data → news items (object or array)
  //   companyInsights.insights → array of grouped insight items
  //   companyInsights.data → signals (rendered separately by SellingSignals, ignore here)
  //   deepDiveIntel → competitor_names[], tools_in_stack[], revenue_data{}
  const company = companyData?.company_data || {};
  const webData = companyData?.web_data;
  const companyNews = Array.isArray(webData)
    ? webData
    : (webData && typeof webData === "object" ? (webData.data || []) : []);

  const insightsList = Array.isArray(companyInsights?.insights) ? companyInsights.insights : [];

  const companyName = company.name || "";
  if (!companyName && insightsList.length === 0 && companyNews.length === 0) return null;

  const competitors = deepDiveIntel?.competitor_names || [];
  const tools = deepDiveIntel?.tools_in_stack || [];
  const revenue = deepDiveIntel?.revenue_data || {};

  // Split tools into (a) seller-competitors-in-stack (displacement signal) vs
  // (b) other tools (informational). Match is case-insensitive against the
  // seller_intelligence.competitor_map.direct+adjacent Set passed from parent.
  // ALSO scan the prospect's own competitor_names list — sometimes the deep-
  // dive extractor surfaces seller-competitors there too (e.g. "Outreach" in
  // a company's blog mention) — those are still displacement signals.
  const sellerCompSet = sellerCompetitorNames instanceof Set
    ? sellerCompetitorNames
    : new Set();
  const isSellerCompetitor = (name) =>
    sellerCompSet.size > 0 && sellerCompSet.has(String(name).trim().toLowerCase());
  const sellerCompetitorsInStack = [
    ...tools.filter(isSellerCompetitor),
    ...competitors.filter(isSellerCompetitor)
  ];
  // Dedup (a tool may appear in both lists)
  const sellerCompetitorsInStackUnique = Array.from(new Set(sellerCompetitorsInStack));
  const regularTools = tools.filter(t => !isSellerCompetitor(t));
  const accountPeers = competitors.filter(c => !isSellerCompetitor(c));

  // Group news by category
  const groupedNews = companyNews.reduce((acc, item) => {
    const cat = item?.category || "other";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  // Group static insights by category too (insights array often has category-tagged items)
  const groupedInsights = insightsList.reduce((acc, item) => {
    const cat = item?.category || "other";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  return (
    <>
      {/* Company Overview Table */}
      <div className="drSection">
        <div className="drSectionHeader">
          <span className="drSectionIcon">🏢</span>
          <h3>{companyName} Overview</h3>
          {accountId && (
            <a
              href={`/accounts/${accountId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="drSectionLink"
              title="Open full Account Intel"
              style={{ marginLeft: "auto", fontSize: "12px", color: "#7c3aed", textDecoration: "none", fontWeight: 500 }}
            >
              View full Account Intel →
            </a>
          )}
        </div>
        {/* Competitors & Tech Stack — three-section callout, ordered by
            actionability for the seller:
              1. SELLER COMPETITORS IN THEIR STACK (red, urgent displacement)
              2. WHO THEY COMPETE WITH (blue, positioning context)
              3. OTHER TOOLS (neutral, informational) */}
        {(sellerCompetitorsInStackUnique.length > 0 || accountPeers.length > 0 || regularTools.length > 0) && (
          <div className="drStackCallout">
            {sellerCompetitorsInStackUnique.length > 0 && (
              <div className="drStackCallout__row drStackCallout__row--displacement">
                <div className="drStackCallout__head">
                  <span className="drStackCallout__icon">🚨</span>
                  <span className="drStackCallout__label">Seller competitors in their stack</span>
                  <span className="drStackCallout__count">{sellerCompetitorsInStackUnique.length}</span>
                </div>
                <p className="drStackCallout__hint">
                  These tools compete with the seller — strongest displacement angle. Lead with these in outreach.
                </p>
                <div className="drStackCallout__pills">
                  {sellerCompetitorsInStackUnique.map((c, i) => (
                    <span key={i} className="drStackPill drStackPill--displacement">{c}</span>
                  ))}
                </div>
              </div>
            )}
            {accountPeers.length > 0 && (
              <div className="drStackCallout__row drStackCallout__row--competitor">
                <div className="drStackCallout__head">
                  <span className="drStackCallout__icon">🏟️</span>
                  <span className="drStackCallout__label">Who they compete with</span>
                  <span className="drStackCallout__count">{accountPeers.length}</span>
                </div>
                <p className="drStackCallout__hint">
                  Companies in {companyName || "this account"}'s category — position the seller as helping them win against these.
                </p>
                <div className="drStackCallout__pills">
                  {accountPeers.map((c, i) => (
                    <span key={i} className="drStackPill drStackPill--competitor">{c}</span>
                  ))}
                </div>
              </div>
            )}
            {regularTools.length > 0 && (
              <div className="drStackCallout__row drStackCallout__row--tools">
                <div className="drStackCallout__head">
                  <span className="drStackCallout__icon">🔧</span>
                  <span className="drStackCallout__label">Tools they mention using</span>
                  <span className="drStackCallout__count">{regularTools.length}</span>
                </div>
                <p className="drStackCallout__hint">
                  Tech surfaced from {companyName || "this account"}'s content / JDs.
                </p>
                <div className="drStackCallout__pills">
                  {regularTools.map((t, i) => (
                    <span key={i} className="drStackPill drStackPill--tools">{t}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {company.description && (
          <p style={{ fontSize: "13px", color: "#555", marginBottom: "12px", lineHeight: 1.5 }}>
            {company.description}
          </p>
        )}
        <table className="drTable">
          <thead><tr><th>Attribute</th><th>Details</th></tr></thead>
          <tbody>
            <RenderRow label="Founded" value={company.founded} />
            <RenderRow label="Headquarters" value={formatHQ(company)} />
            <RenderRow label="Team Size" value={company.team_size || company.staff?.total} />
            <RenderRow label="Industry" value={company.industry} />
            <RenderRow label="Revenue" value={company.revenue} />
            {revenue.arr && <tr><td>ARR</td><td>{revenue.arr}</td></tr>}
            <RenderRow label="LinkedIn Followers" value={company.followers} />
            <RenderRow label="Website" value={company.website} />
            <RenderRow label="Phone" value={company.phone} />
            <RenderRow label="Specialities" value={company.specialities} />
          </tbody>
        </table>

        <SectionSources
          serpLinks={serpLinks}
          labels={[
            "COMPANY_REVIEWS",
            "COMPANY_LEADERSHIP_CHANGES",
            "COMPANY_COMPETITIVE_SIGNALS",
            "COMPANY_LATKA_DATA",
            "COMPANY_JOB_POSTINGS",
            "COMPANY_ORG_CHART",
            "COMPANY_FINANCIAL_DATA",
            "COMPANY_GLASSDOOR_REVIEWS",
            "COMPANY_CUSTOMERS"
          ]}
          max={12}
        />
      </div>

      {/* Company Insights (from companyInsights.insights[]) */}
      {Object.keys(groupedInsights).length > 0 && (
        <div className="drSection">
          <div className="drSectionHeader">
            <span className="drSectionIcon">💡</span>
            <h3>{companyName} Insights</h3>
          </div>
          <div className="drNewsList">
            {Object.entries(groupedInsights).map(([category, items]) => (
              <div key={category} className="drNewsCategory">
                <h4 className="drNewsCategoryTitle">🔹 {formatKey(category)}</h4>
                {items.map((item, idx) => (
                  <div key={idx} className="drNewsItem">
                    <p className="drNewsText">
                      {item.date && <span className="drNewsDate">{item.date}: </span>}
                      {item.insight || item.insights || item.text}
                    </p>
                    {item.associated_links?.length > 0 && (
                      <div className="drNewsSources">
                        {item.associated_links.map((link, li) => {
                          const domain = (() => { try { return new URL(link).hostname; } catch { return ""; } })();
                          return (
                            <a key={li} href={link} target="_blank" rel="noopener noreferrer" className="drNewsSourceLink">
                              <img src={`https://www.google.com/s2/favicons?domain=${domain}&sz=16`} alt="" />
                            </a>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Company News (from companyData.web_data) */}
      {companyNews.length > 0 && (
        <div className="drSection">
          <div className="drSectionHeader">
            <span className="drSectionIcon">📰</span>
            <h3>{companyName} News</h3>
          </div>
          <div className="drNewsList">
            {Object.entries(groupedNews).map(([category, items]) => (
              <div key={category} className="drNewsCategory">
                <h4 className="drNewsCategoryTitle">🔹 {formatKey(category)}</h4>
                {items.map((item, idx) => (
                  <div key={idx} className="drNewsItem">
                    <p className="drNewsText">
                      {item.date && <span className="drNewsDate">{item.date}: </span>}
                      {item.insight || item.insights || item.text || item.title}
                    </p>
                    {item.associated_links?.length > 0 && (
                      <div className="drNewsSources">
                        {item.associated_links.map((link, li) => {
                          const domain = (() => { try { return new URL(link).hostname; } catch { return ""; } })();
                          return (
                            <a key={li} href={link} target="_blank" rel="noopener noreferrer" className="drNewsSourceLink">
                              <img src={`https://www.google.com/s2/favicons?domain=${domain}&sz=16`} alt="" />
                            </a>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
});

export default CompanyOverview;
